import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { defer, iif, Observable, of } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { GenericDialogService } from '../../../services/ui/generic-dialog.service';
import { NoticeActionDto, NoticeActionOverviewDto } from '../../api/notices';
import {
  VorlagenMetadataConfirmedEvent,
  VorlagenNameDialogComponent,
} from '../../vorlagen-uebersicht/vorlagen-name-dialog/vorlagen-name-dialog.component';
import { TemplateNameAndDescriptionService } from '../bekanntmachungen-auswahl/template-name-and-description.service';
import { NoticeOverviewViewModel } from '../NoticeOverviewViewModel';
import { NoticeActionModel } from './notice-action.model';

@Component({
  selector: 'app-bekanntmachung-aktionen',
  templateUrl: './bekanntmachung-aktionen.component.html',
  styleUrls: ['./bekanntmachung-aktionen.component.scss'],
})
export class BekanntmachungAktionenComponent implements OnInit {
  @ViewChild(VorlagenNameDialogComponent) vorlagenNameDialog!: VorlagenNameDialogComponent;

  @Input() notice: NoticeOverviewViewModel;

  @Output() clickActionType = new EventEmitter<{
    action: NoticeActionDto;
    subTypeId?: string;
    notice: NoticeOverviewViewModel
  }>();

  ActionType = NoticeActionDto.TypeEnum;

  iconActions = [
    NoticeActionDto.TypeEnum.EditDraft,
    NoticeActionDto.TypeEnum.ShowPreview,
    NoticeActionDto.TypeEnum.ExportCn,
  ];
  contextMenuActions = [
    NoticeActionDto.TypeEnum.DeleteDraft,
    NoticeActionDto.TypeEnum.CreateTemplate,
    NoticeActionDto.TypeEnum.Stop,
    NoticeActionDto.TypeEnum.EditSentChangeNotice,
    NoticeActionDto.TypeEnum.EditSentNotice,
    NoticeActionDto.TypeEnum.CreateChangeNotice,
    NoticeActionDto.TypeEnum.CreateCn,
    NoticeActionDto.TypeEnum.CreateCan,
    NoticeActionDto.TypeEnum.CreateCanModif,
  ];

  iconVm: NoticeActionModel[] = [];
  contextMenuVm: NoticeActionModel[] = [];

  constructor(
    private genericDialogService: GenericDialogService,
    private templateNameAndDescriptionService: TemplateNameAndDescriptionService,
  ) {
  }

  ngOnInit(): void {
    this.iconVm = this.createViewModels(this.iconActions);
    this.contextMenuVm = this.createViewModels(this.contextMenuActions);
  }

  onActionClick(menuItem: NoticeActionModel): void {
    if (menuItem.type === NoticeActionDto.TypeEnum.CreateTemplate) {
      this.showCreateTemplateDialog();
    } else {
      this.clickActionType.emit({ action: menuItem.action.action, subTypeId: menuItem.subTypeId, notice: this.notice });
    }
  }

  createTemplate($event: VorlagenMetadataConfirmedEvent): void {
    this.templateNameAndDescriptionService.setValues(
      $event.metadata.name,
      $event.metadata.description,
    );
    this.clickActionType.emit({ action: { type: NoticeActionDto.TypeEnum.CreateTemplate }, notice: this.notice });
  }

  anyNachfolgerActionVisible(): boolean {
    return this.notice.notice.actions.some(
      action => [
        NoticeActionDto.TypeEnum.CreateCn,
        NoticeActionDto.TypeEnum.CreateCan,
        NoticeActionDto.TypeEnum.CreateCanModif,
      ].includes(action.action.type),
    );
  }

  private showCreateTemplateDialog(): void {
    const navigate$ = iif(
      () => this.notice.notice.latestSdkVersion,
      of(true),
      this.confirmTemplateModalDialog(),
    );

    navigate$
      .pipe(
        filter(Boolean),
        map(() => this.vorlagenNameDialog.openDialog()),
      )
      .subscribe();
  }

  private confirmTemplateModalDialog(): Observable<boolean> {
    return defer(() =>
      this.genericDialogService.confirmWithWarningDialog(
        'WARNUNG: Aktualisierung des eForms-Standards',
        'Akzeptieren',
        'Abbrechen',
        '<b>Grund der Warnung</b><br>' +
        'Der eForms-Standard wurde aktualisiert, seit diese Bekanntmachung das letzte Mal bearbeitet wurde.<br><br>' +
        '<b>Was ist zu beachten?</b><br>' +
        'Die Vorlage wird automatisch gemäß des aktuellen Standards generiert. Bitte überprüfen Sie, ob alle wichtigen Daten in die Vorlage übernommen wurden. Unter Umständen kann es bei der automatischen Vorbefüllung der Felder mit den Werten aus der ursprünglichen Bekanntmachung zu Fehlern kommen.',
      ),
    );
  }

  private createViewModels(types: NoticeActionDto.TypeEnum[]): NoticeActionModel[] {
    return types.map(type => {
      const action = this.notice.notice.actions.find(a => a.action.type === type);
      if (!!action) {
        const isLatestNoticeInChain = this.notice.isNewestNoticeForSubtypeId;
        return action.subTypeIds && action.subTypeIds.length > 0
          ? action.subTypeIds.map(subTypeId => this.createViewModel(type, action, isLatestNoticeInChain, subTypeId))
          : this.createViewModel(type, action, isLatestNoticeInChain);
      }
    }).filter(Boolean).flat();
  }

  private createViewModel(type: NoticeActionDto.TypeEnum, action: NoticeActionOverviewDto, isLatestNoticeInChain: boolean, subTypeId?: string): NoticeActionModel {
    return {
      type,
      subTypeId,
      action,
      enabled: action.enabled,
      iconName: this.getIconNameByType(type),
      tooltip: this.getTooltipByTypeAndEnabled(type, action.enabled, isLatestNoticeInChain),
    };
  }

  private getIconNameByType(type: NoticeActionDto.TypeEnum): string | undefined {
    switch (type) {
      case NoticeActionDto.TypeEnum.EditDraft:
        return 'edit';
      case NoticeActionDto.TypeEnum.ShowPreview:
        return 'preview';
      case NoticeActionDto.TypeEnum.ExportCn:
        return 'download';
      case NoticeActionDto.TypeEnum.DeleteDraft:
        return 'delete';
      case NoticeActionDto.TypeEnum.Stop:
        return 'undo';
      case NoticeActionDto.TypeEnum.CreateCn:
      case NoticeActionDto.TypeEnum.CreateCan:
      case NoticeActionDto.TypeEnum.CreateCanModif:
        return 'library_add';
    }
  }

  private readonly ENABLED_TOOLTIPS = {
    EDIT_DRAFT: 'Entwurf bearbeiten',
    SHOW_PREVIEW: 'Vorschau anzeigen',
    EXPORT_CN: 'Bekanntmachung exportieren',
  };

  private readonly DISABLED_TOOLTIPS = {
    EDIT_DRAFT: 'Entwurf bearbeiten',
    SHOW_PREVIEW: 'Vorschau anzeigen',
    EXPORT_CN: 'Bekanntmachung exportieren',
    DELETE: 'Versendete Bekanntmachungen können nicht gelöscht werden.',
    TEMPLATE: 'Für diesen Bekanntmachungstyp kann keine Vorlage erstellt werden.',
    STOP: 'Bekanntmachungen können nur in den TED-Status "PENDING" und "ACCEPTED" zurückgezogen werden.',
    EDIT_SENT_TED: 'Bekanntmachungen können nur im TED-Status "STOPPED" aktualisiert werden.',
    EDIT_SENT_LATEST: 'Nur die aktuellste Version einer Bekanntmachung kann aktualisiert werden.',
    CHANGE_TED: 'Bekanntmachungen können erst nach der Veröffentlichung bei TED korrigiert werden.',
    CHANGE_LATEST: 'Nur die aktuellste Version einer Bekanntmachung kann korrigiert werden.',
    CREATE_SUCCESSOR_TED: 'Nachfolger können erst nach der Veröffentlichung der ursprünglichen Bekanntmachung bei TED erstellt werden.',
    CREATE_SUCCESSOR_LATEST: 'Nur für die aktuellste Version einer Bekanntmachung kann ein Nachfolger angelegt werden.',
  };

  private getTooltipByTypeAndEnabled(type: NoticeActionDto.TypeEnum, enabled: boolean, isLatestNoticeInChain: boolean): string | undefined {
    if (enabled) {
      return this.getEnabledTooltip(type);
    }

    return this.getDisabledTooltip(type, isLatestNoticeInChain);
  }

  private getEnabledTooltip(type: NoticeActionDto.TypeEnum): string | undefined {
    switch (type) {
      case NoticeActionDto.TypeEnum.EditDraft:
        return this.ENABLED_TOOLTIPS.EDIT_DRAFT;
      case NoticeActionDto.TypeEnum.ShowPreview:
        return this.ENABLED_TOOLTIPS.SHOW_PREVIEW;
      case NoticeActionDto.TypeEnum.ExportCn:
        return this.ENABLED_TOOLTIPS.EXPORT_CN;
      default:
        return undefined;
    }
  }

  private getDisabledTooltip(type: NoticeActionDto.TypeEnum, isLatestNoticeInChain: boolean): string | undefined {
    switch (type) {
      case NoticeActionDto.TypeEnum.EditDraft:
        return this.DISABLED_TOOLTIPS.EDIT_DRAFT;
      case NoticeActionDto.TypeEnum.ShowPreview:
        return this.DISABLED_TOOLTIPS.SHOW_PREVIEW;
      case NoticeActionDto.TypeEnum.ExportCn:
        return this.DISABLED_TOOLTIPS.EXPORT_CN;
      case NoticeActionDto.TypeEnum.DeleteDraft:
        return this.DISABLED_TOOLTIPS.DELETE;
      case NoticeActionDto.TypeEnum.CreateTemplate:
        return this.DISABLED_TOOLTIPS.TEMPLATE;
      case NoticeActionDto.TypeEnum.Stop:
        return this.DISABLED_TOOLTIPS.STOP;
      case NoticeActionDto.TypeEnum.EditSentNotice:
      case NoticeActionDto.TypeEnum.EditSentChangeNotice:
        return isLatestNoticeInChain ? this.DISABLED_TOOLTIPS.EDIT_SENT_TED : this.DISABLED_TOOLTIPS.EDIT_SENT_LATEST;
      case NoticeActionDto.TypeEnum.CreateChangeNotice:
        return isLatestNoticeInChain ? this.DISABLED_TOOLTIPS.CHANGE_TED : this.DISABLED_TOOLTIPS.CHANGE_LATEST;
      case NoticeActionDto.TypeEnum.CreateCn:
      case NoticeActionDto.TypeEnum.CreateCan:
      case NoticeActionDto.TypeEnum.CreateCanModif:
        return isLatestNoticeInChain ? this.DISABLED_TOOLTIPS.CREATE_SUCCESSOR_TED : this.DISABLED_TOOLTIPS.CREATE_SUCCESSOR_LATEST;
      default:
        return undefined;
    }
  }
}
